/**
 * THE LANDFORM FIELD — sea, beaches, plains and mountains, and everything derived from that shape.
 *
 * Lifted out of TerrainGenerator verbatim in C3 of docs/node-graph-refactor-plan.md, which is why
 * the comments below read as they did: every one of them records a measurement or a defect, and
 * rewriting them while moving them would have thrown that away. What changed is OWNERSHIP — the
 * field holds its own noise, its own config-derived state (the derived base frequency, the warp
 * amplitude, the compiled elevation curve, the sea percentile) and its own two slope memos, where
 * the generator held all of it and 400 lines besides.
 *
 * THE SHARED COLUMN-HEIGHT MEMO STAYS IN THE COORDINATOR, and this field reaches it through
 * `heightAt`. Three subsystems ask for the same columns, and it is keyed on exact floats so a grid
 * query hits and an off-grid one recomputes exactly; splitting it per field would multiply it by the
 * number of askers. The two memos here are different — slope and post-detail slope are landform's
 * alone, and nothing else asks for them.
 *
 * THE SEED BLOCK IS PASSED IN, and consumed in the order the constructor lists. `seedChain.test.ts`
 * pins the result: inserting a field in that list re-rolls every field after it, which is to say it
 * silently changes every world.
 */
import FastNoiseLite from 'fastnoise-lite';
import { VOXEL_SCALE } from '../../voxel/constants.js';
import { smoothstep, smoothstepSlope } from '../../util/math.js';
import { Curve, DEFAULT_LANDFORM_CURVE } from '../Curve.js';
/**
 * Landform wavelength as a multiple of the relief. The base wavelength DERIVES from the relief, so
 * slopes are scale-invariant: raising mountain height widens the landmasses by the same factor
 * instead of just stretching them vertically.
 */
export const LANDFORM_ASPECT = 3.58;
/** Flatness exponent range: the field drives an exponent between these, mountains to plains. */
const LANDFORM_FLATNESS_SPREAD = [2.0, 0.6];
/** Minimum beach band in voxels, so the sand shelf survives a small world. */
const MIN_BEACH_VOXELS = 3;
/** Absolute base frequency the surface-detail noise is scaled from. */
const LANDFORM_DETAIL_BASE_FREQ = 0.03;
/**
 * The band of terrace STRENGTH over which the sigmoid fades in, and below which it is skipped.
 *
 * THE SKIP HAS TO BE CONTINUOUS WITH WHAT IT SKIPS, and for its whole life it was not. The exponent
 * is `n = 1 + s*(sharpness - 1)`, so at the old bare cutoff of s = 0.05 with the default sharpness
 * of 32 the sigmoid was already n = 2.55 — a real quantizer, not the identity the comment claimed.
 * The height therefore stepped by up to 2.96 m ACROSS THE CUT, and the cut is a contour of the
 * step-mask noise, so the artifact was a set of long smooth curves cutting across the terrain as
 * sharp vertical walls, unrelated to the elevation contours the real risers follow. At the default
 * coverage of 0.25 that contour sits at mask noise -0.072 — essentially the field's median, which is
 * exactly where contour length is greatest, so the world was as full of them as it could be.
 *
 * Fading `mix` to zero AT the cutoff makes the skip exact rather than approximate: below LO the
 * terrace contributes nothing, and just above it the ramp contributes nothing either. HI is set so
 * the ramp spans ~35 m of ground at the default mask wavelength — gentle enough to read as terrain,
 * short enough that the stepped districts still have hard edges.
 */
const STEP_FADE_LO = 0.05;
const STEP_FADE_HI = 0.20;
export class LandformField {
    cfg;
    heightAt;
    warp; // large-scale domain warp, fed as the base field's 3rd axis
    base; // one low-freq continental fBm -> the landmass shape
    baseRidged; // the same field as RIDGED, blended in by `ridged`
    stepMask; // where terracing is sharp vs absent
    wobble; // post-terrace vertical wobble (treads aren't dead flat)
    flatness; // mountain-vs-plain character (drives the height exponent)
    detail; // dedicated surface-detail fBm (own explicit frequency)
    /** Warp displacement in world units per unit of warp noise — a fixed fraction of a wavelength. */
    warpAmp = 0;
    /** The derived continental base frequency (1/m). Cached so the sea-coverage grid can size its span. */
    baseFreq = 0;
    /** Compiled elevation curve: continental noise 0..1 -> a height offset relative to sea level. */
    curveFn = new Curve(DEFAULT_LANDFORM_CURVE);
    seaU0 = 0.5; // curve input where the elevation offset crosses sea level
    seaUThreshold = 0.5; // noise percentile that should map to that crossing
    /** Per-column macro slope, and the post-detail slope angle. Landform's own. */
    slopeCache = new Map();
    slopeCacheEntries = 0;
    detailSlopeCache = new Map();
    detailSlopeCacheEntries = 0;
    /** Combined world+land size divisor: bigger scale -> lower frequency -> larger features. */
    sizeScale = 1;
    /**
     * @param heightAt the coordinator's memoised column height. The post-detail slope probes through
     *   it rather than recomputing, so four probes per column are cache hits instead of four full
     *   macro+slope+detail evaluations.
     */
    constructor(cfg, seed, sizeScale, heightAt) {
        this.cfg = cfg;
        this.heightAt = heightAt;
        // THE ORDER IS THE WORLD. Each field takes the next seed in the block; seedChain.test.ts pins it.
        let s = seed;
        this.warp = fnl(s++, 3); // 3 octaves so the swirl has detail at a few scales
        this.base = fnl(s++, 1); // octave count is config-driven, applied in updateConfig
        this.detail = fnl(s++, 4); // several octaves -> fine rocks/rubble at multiple scales
        this.baseRidged = fnl(s++, 1, FastNoiseLite.FractalType.Ridged);
        this.stepMask = fnl(s++, 1);
        this.wobble = fnl(s++, 2); // the reference uses 3; the 3rd is invisible on a smooth undulation
        this.flatness = fnl(s++, 2);
        this.sizeScale = sizeScale;
        this.updateConfig();
    }
    setConfig(cfg, sizeScale) {
        this.cfg = cfg;
        this.sizeScale = sizeScale;
        this.updateConfig();
        this.clearCaches();
    }
    /**
     * Re-seed in place, keeping the block order. The sea percentile is measured FROM the continental
     * noise, so a reseed invalidates it — without this, a seed-only reconfigure kept the old seed's
     * threshold and the sea ratio drifted off the requested percentage.
     */
    setSeed(seed) {
        let s = seed;
        for (const n of [this.warp, this.base, this.detail, this.baseRidged, this.stepMask, this.wobble, this.flatness]) {
            n.SetSeed(s++);
        }
        this.computeSeaRemap();
        this.clearCaches();
    }
    clearCaches() {
        this.slopeCache.clear();
        this.slopeCacheEntries = 0;
        this.detailSlopeCache.clear();
        this.detailSlopeCacheEntries = 0;
    }
    /**
     * Below the plains-to-beach edge nothing is built: a skyline standing in the sea is the first
     * thing you would see. A suppression contributor in the sense of the plan's §3.3 seam, and the
     * only one that is not the boundary wall.
     */
    suppressed(worldX, worldZ) {
        if (!this.cfg.landformEnabled)
            return false; // no waterline -> nothing to suppress
        return this.heightAt(worldX, worldZ) <= this.cfg.groundLevel + this.beachBandVox();
    }
    /** Landform noise frequencies derive from the land base frequency and the master/land scale (size
     *  divisor). The amplitude/curve knobs live in config and are read per-sample. Also (re)compiles the
     *  elevation curve from config.landformCurve. */
    updateConfig() {
        const t = this.cfg;
        const sizeDiv = this.sizeScale;
        // Wavelength DERIVES from the relief (see LANDFORM_ASPECT), so slopes are scale-invariant: raising
        // mountainHeight widens the landmasses by the same factor. Note sizeDiv is applied to the relief here
        // rather than dividing the frequency — the relief is already scaled by it in elevationCurve, so
        // dividing again would double-scale the world's width relative to its height.
        const reliefM = Math.max(1, t.landformMountainHeight * VOXEL_SCALE * sizeDiv);
        const lambda = reliefM * LANDFORM_ASPECT / (t.landformScale > 0 ? t.landformScale : 1);
        const baseFreq = 1 / lambda;
        this.baseFreq = baseFreq;
        const warpFreq = baseFreq * (t.landformWarpScale > 0 ? t.landformWarpScale : 1);
        const octaves = Math.max(1, Math.min(12, Math.round(t.landformOctaves ?? 10)));
        this.base.SetFrequency(baseFreq);
        this.base.SetFractalOctaves(octaves);
        this.baseRidged.SetFrequency(baseFreq);
        this.baseRidged.SetFractalOctaves(octaves);
        this.warp.SetFrequency(warpFreq);
        // Modulation-field frequencies are expressed as MULTIPLES of the base frequency, matching the
        // reference's ratios (its step mask sits at λ/16, wobble at λ/64, flatness at λ/2), so they keep their
        // relationship to feature size at any world scale instead of drifting when mountain height changes.
        this.stepMask.SetFrequency(baseFreq * Math.max(0.01, t.landformStepMaskScale ?? 16));
        this.wobble.SetFrequency(baseFreq * Math.max(0.01, t.landformStepWobbleScale ?? 64));
        this.flatness.SetFrequency(baseFreq * Math.max(0.01, t.landformFlatnessScale ?? 2));
        // FastNoiseLite multiplies coordinates by frequency internally, so dividing the displacement by
        // baseFreq makes it a fraction of ONE WAVELENGTH — automatically right at any scale.
        this.warpAmp = (t.landformWarpFraction ?? 0) / baseFreq;
        // Detail frequency is an ABSOLUTE rubble scale (not tied to the tiny continental frequency), so it
        // reads as rocks/rubble. Divided by the world size so it stays proportional — but the divisor is
        // FLOORED at 1 so shrinking the world (masterScale < 1) can't push the detail wavelength below its
        // scale-1 value (~3 m). Without this floor, at 0.1 the wavelength drops to ~0.3 m (sub-voxel), the
        // surface aliases to noise, and the post-detail slope reads vertical everywhere → rock everywhere
        // (the "green contour lines", missing beach/snow).
        const detailDiv = Math.max(sizeDiv, 1);
        this.detail.SetFrequency(LANDFORM_DETAIL_BASE_FREQ * (t.landformDetailFrequency > 0 ? t.landformDetailFrequency : 1) / detailDiv);
        const pts = t.landformCurve && t.landformCurve.length >= 2 ? t.landformCurve : DEFAULT_LANDFORM_CURVE;
        this.curveFn = new Curve(pts);
        this.computeSeaRemap();
    }
    /** Sea coverage: sample the continental noise distribution once and remap the elevation-curve input so
     *  exactly `seaCoveragePercent` of columns fall below sea, regardless of the curve shape or world scale.
     *  Sets seaUThreshold (the noise percentile, in curve-input space) and seaU0 (the curve's sea crossing);
     *  elevationCurve() stretches its input so uThreshold → u0. */
    computeSeaRemap() {
        const t = this.cfg;
        // Curve sea-crossing u0 (monotone offset: negative below sea, positive above) via bisection.
        let lo = 0, hi = 1;
        if (this.curveFn.eval(0) >= 0) {
            this.seaU0 = 0;
        }
        else if (this.curveFn.eval(1) <= 0) {
            this.seaU0 = 1;
        }
        else {
            for (let k = 0; k < 24; k++) {
                const mid = (lo + hi) / 2;
                if (this.curveFn.eval(mid) < 0)
                    lo = mid;
                else
                    hi = mid;
            }
            this.seaU0 = (lo + hi) / 2;
        }
        // Percentile threshold of the continental field over a deterministic grid (one-time, ~8k evals at
        // construction — never on the per-column path).
        //
        // Two things this MUST match about the real sampling or the quantile describes a different field than
        // the terrain and the sea ratio drifts:
        //   1. the WARPED 3D sample, exactly as macroSmooth does it;
        //   2. enough wavelengths to be a distribution rather than a handful of blobs. The span used to be a
        //      fixed 4000 m, which covered ~8 wavelengths of the old ~460 m landforms but only ~2 once the
        //      wavelength derives from a kilometre-scale relief — so it now scales with the wavelength.
        const cov = Math.min(0.95, Math.max(0, (t.seaCoveragePercent ?? 45) / 100));
        const N = 64;
        const span = Math.max(4000, 8 / Math.max(1e-9, this.baseFreq));
        const vals = [];
        for (let i = 0; i < N; i++)
            for (let j = 0; j < N; j++) {
                const gx = (i / (N - 1) - 0.5) * span, gz = (j / (N - 1) - 0.5) * span;
                vals.push(this.base.GetNoise(gx, gz, this.warp.GetNoise(gx, gz) * this.warpAmp));
            }
        vals.sort((a, b) => a - b);
        const noiseThresh = vals[Math.min(vals.length - 1, Math.floor(cov * vals.length))];
        this.seaUThreshold = (noiseThresh + 1) * 0.5; // noise [-1,1] → curve input [0,1]
    }
    /**
     * Landform height model (sea/beach/plains/mountains) — one continental field, strongly warped, run
     * through an elevation curve, plus elevation-modulated detail. Returns height in voxels. Called only
     * when landformEnabled; result is memoised by the sampleHeight cache above.
     */
    height(worldX, worldZ) {
        const t = this.cfg;
        // One flatness sample for the whole column, shared with the slope probes below.
        const flatness = this.flatnessAt(worldX, worldZ);
        const macro = this.macro(worldX, worldZ, flatness);
        // Read IMMEDIATELY after the macro call, which is what sets it (terraceHeight writes it as a side
        // channel to avoid allocating a tuple per column on the hottest path in the generator).
        const gain = this.terraceGain;
        // Surface detail: a dedicated noise (its own frequency, set in updateLandformConfig) whose amplitude
        // is driven by slope — flat ground gets a small floor of texture, steep faces get jagged ruggedness
        // (footholds to climb). Applied EVERYWHERE, including steep seabed under the sea (the slope term is
        // near-zero on flats, so beaches/plains stay gentle). Amplitude scales with the world.
        const vscale = this.sizeScale;
        // Detail keys off the slope of the FINAL surface, not the smooth field underneath it. Terracing is a
        // function of the height VALUE, so the chain rule gives that for free: the terraced slope is the smooth
        // slope times the terrace's vertical gain. No extra probes, and sharper than finite differences, which
        // would smear a riser across the whole 4 m probe span.
        //
        // This is what stops rubble appearing on flat ground: a tread has gain ~0 however steep the hillside
        // underneath it, while a riser multiplies the slope up and gets the full jagged amount.
        const slopeDeg = Math.atan(this.slope(worldX, worldZ, flatness) * gain) * (180 / Math.PI);
        const t01 = Math.min(1, slopeDeg / 90);
        const rugged = t01 * t01;
        // Detail amplitude is global (not per-biome for now): flat floor + steep ruggedness ramp.
        const amp = (t.landformDetailFlat + t.landformDetailSteep * rugged) * vscale;
        const h = amp <= 0 ? macro : macro + this.detail.GetNoise(worldX, worldZ) * amp;
        return this.applyBeachLip(h);
    }
    /** Beach band height in voxels above sea level. Scales with the world (like the rest of the relief)
     *  but is floored at MIN_BEACH_VOXELS so the sand shelf stays visible at small master scales (at 0.1
     *  the scaled band would otherwise collapse below a single voxel and the beach would vanish). */
    beachBandVox() {
        const t = this.cfg;
        return Math.max(t.landformBeachWidthFrac * t.landformMountainHeight * this.sizeScale, MIN_BEACH_VOXELS);
    }
    /** 1-voxel beach lip: wherever the surface sits within a voxel of the waterline, duck it to sea−1 so
     *  the flat water plane always covers the wet sand (kills z-fighting where sand ≈ sea). A fixed
     *  absolute voxel (not scaled), applied only to the visible surface — not the macro height used by
     *  slope estimation and river/path channels. */
    applyBeachLip(h) {
        const sea = this.cfg.groundLevel;
        return (h > sea - 1 && h < sea + 1) ? sea - 1 : h;
    }
    /**
     * Macro landform height WITHOUT the terrace steps (warp + curve, no surface detail) — the smooth field.
     *
     * Used wherever a gradient or a channel bed is needed: pathway/river beds (so channels aren't chopped
     * up), the slope estimate that drives detail amplitude, and river tracing — a terraced tread is
     * perfectly flat, so gradient descent over the stepped field would stall on it.
     *
     * The warp is fed as the THIRD AXIS of a 3D sample rather than as additive per-axis XZ offsets. That is
     * deliberate and not interchangeable: OpenSimplex2's 3D lattice rotates that displacement into all three
     * axes, which is what produces the characteristic diagonal swirl. A conventional `x += warp` offset of
     * the same magnitude looks materially different.
     */
    macroSmooth(worldX, worldZ, flatness = -1) {
        // `flatness` is threaded in by callers that already sampled it for this column (see
        // sampleLandformHeight). It is a very low-frequency field, so re-sampling it at each ±4 m slope probe
        // was ~10 wasted noise calls per column — the single biggest cost in the first cut of this change.
        const f = flatness >= 0 ? flatness : this.flatnessAt(worldX, worldZ);
        const w = this.warp.GetNoise(worldX, worldZ) * this.warpAmp;
        let n = this.base.GetNoise(worldX, worldZ, w);
        // Ridged blend: sharp alpine ridgelines instead of rounded FBm. Skipped entirely at 0 (the default), so
        // it costs nothing unless asked for — it is a full extra 3D multi-octave sample, the priciest call here.
        const ridged = this.cfg.landformRidged ?? 0;
        if (ridged > 0) {
            // Negated: FastNoiseLite's Ridged puts its ridges at the TOP of the range, and negating turns the
            // sharp features into peaks rather than crevasses.
            const r = -this.baseRidged.GetNoise(worldX, worldZ, w);
            n = n + (r - n) * Math.min(1, ridged);
        }
        return this.elevationCurve(n, f);
    }
    /** The flatness field at a column, 0..1 — see the exponent in elevationCurve. Returns the neutral 0.5
     *  without sampling when the feature is off, so disabling it costs nothing rather than a wasted call. */
    flatnessAt(worldX, worldZ) {
        if (!(this.cfg.landformFlatnessAmount > 0))
            return 0.5;
        return (this.flatness.GetNoise(worldX, worldZ) + 1) * 0.5;
    }
    /**
     * Macro landform height as the player SEES it: the smooth field with terrace steps applied. This is the
     * field the visible surface (and hence the material cascade) is built on.
     */
    macro(worldX, worldZ, flatness = -1) {
        const smooth = this.macroSmooth(worldX, worldZ, flatness);
        const terraced = this.terraceHeight(smooth, this.stepStrengthAt(worldX, worldZ));
        return this.applyStepWobble(terraced, worldX, worldZ);
    }
    /**
     * Terrace the macro height into flat treads separated by near-vertical risers — the stepped, cliff-banded
     * mountainsides. Quantizing pivots on SEA LEVEL so treads line up with the waterline at any relief.
     *
     * Nothing else is needed to make the cliffs read as cliffs: the risers are near-vertical, so the existing
     * post-detail slope → rock rule paints them as rock bands over grass/snow treads for free.
     *
     * The band mask is why the shoreline survives. Inside it the height passes through untouched, so the
     * beach shelf, the 1-voxel beach lip and the sea-coverage percentile all behave exactly as before. Its
     * half-width is floored at half a step, NOT just the beach band: with a large step and a small beach
     * width, rounding could otherwise lift a column that sits just below sea level up ONTO sea level,
     * silently draining coastline and breaking the coverage guarantee.
     */
    /**
     * Terrace strength at a column, 0..1 — how sharply stepped this patch of world is.
     *
     * Terracing is NOT applied uniformly: a dedicated low-frequency mask decides where cliffs band and where
     * the land stays smooth, so the world reads as stepped districts rather than one machined staircase. The
     * cubic bias is what makes it decisive — it pushes most of the field toward 0, so "some sharp steps, most
     * of it smooth" instead of a uniform half-terraced mush everywhere.
     */
    stepStrengthAt(worldX, worldZ) {
        const t = this.cfg;
        const cov = Math.max(0, Math.min(1, t.landformStepCoverage ?? 0.5));
        if (cov <= 0)
            return 0;
        const m = (this.stepMask.GetNoise(worldX, worldZ) + 1) * 0.5; // [-1,1] → [0,1]
        const biased = m * m * m; // cubic → mostly smooth world
        // Coverage scales the biased mask: at 1 the strongest districts reach full sharpness.
        return Math.min(1, biased * (cov * 2));
    }
    /**
     * Terrace a height into treads and risers, and record the local vertical GAIN in `terraceGain`.
     *
     * The quantizer is a sigmoid within each step interval rather than `Math.round`:
     *
     *     out = base + step / (1 + ((1-frac)/frac) ^ n),   n = 1 + strength·(sharpness-1)
     *
     * which is the algebraic reduction of the reference's `frac^n / (frac^n + (1-frac)^n)` — identical curve,
     * one `Math.pow` instead of two. Two properties earn it its place over rounding:
     *
     *  - `n = 1` is EXACTLY the identity, so unstepped ground is untouched and the mask can fade terracing in
     *    and out with no seam. Rounding has no such neutral setting.
     *  - the output never leaves `[base, base+step]`, so unlike rounding it cannot shift a column half a step.
     *    That is what previously threatened to lift near-shore columns onto sea level and drain coastline.
     *
     * `terraceGain` is `d(out)/d(h)` = `n·r / (frac·(1-frac)·(1+r)²)`, free from the same `r`. It is the whole
     * reason detail can key off the FINAL surface without extra sampling — see `height`.
     */
    terraceGain = 1;
    terraceHeight(h, strength) {
        this.terraceGain = 1;
        const t = this.cfg;
        const step = (t.landformTerraceStepFrac ?? 0) * t.landformMountainHeight * this.sizeScale;
        const mix = Math.max(0, Math.min(1, t.landformStepMix ?? 0.75));
        if (!(step > 0) || mix <= 0)
            return h;
        const sea = t.groundLevel;
        const rel = h - sea;
        const arel = Math.abs(rel);
        const band = this.beachBandVox();
        // Waterline band passes through untouched: keeps the sand shelf, the beach lip and the sea-coverage
        // percentile behaving exactly as they do without terracing.
        if (arel <= band)
            return h;
        const s = Math.max(0, Math.min(1, strength));
        // Below this the terrace contributes nothing (bandFade/sFade below both reach zero here), and
        // skipping it is ~10x cheaper — which matters because the cubic bias means most of the world
        // lands here. See STEP_FADE_LO for why the skip needs the ramp to be exact rather than close.
        if (s <= STEP_FADE_LO)
            return h;
        // TWO FADES, AND EACH ONE EXISTS BECAUSE ITS EDGE WAS A CLIFF. Both reach exactly zero at the
        // early-outs above, so the skip is continuous rather than merely cheap.
        const sFade = smoothstep(STEP_FADE_LO, STEP_FADE_HI, s);
        // The waterline edge is the same defect one variable over: terracing went from off to full across
        // |rel| = band, stepping the ground by up to 6.8 m along a fixed elevation contour near the shore.
        // Faded over a whole step because the fade is a function of h and therefore enters the SURFACE's
        // own slope — narrower and the `(T − h)·dmix/dh` term below can exceed 1 and invert the
        // heightfield, turning one artifact into a worse one.
        const bandFade = smoothstep(band, band + step, arel);
        const mixEff = mix * sFade * bandFade;
        if (mixEff <= 0)
            return h;
        const sharp = Math.max(1, t.landformStepSharpness ?? 32);
        const n = 1 + s * (sharp - 1);
        const base = Math.floor(rel / step) * step;
        const frac = (rel - base) / step;
        if (!(frac > 1e-6) || frac > 1 - 1e-6)
            return h; // already sitting on a tread edge
        const r = Math.pow((1 - frac) / frac, n);
        // r = Infinity divides to 0, i.e. the bottom of the tread — the answer the old `!isFinite` branch
        // returned directly, and wrongly: it returned it UNBLENDED, ignoring `mix`, where every other path
        // here blends. Only the gain needs the guard, where Infinity/Infinity would be NaN.
        const stepped = sea + base + step / (1 + r);
        const tPrime = isFinite(r) ? (n * r) / (frac * (1 - frac) * (1 + r) * (1 + r)) : 0;
        // GAIN OF THE BLENDED SURFACE: d/dh [ h + (T(h) − h)·mixEff(h) ]
        //                            = 1 + (T′ − 1)·mixEff + (T − h)·dmixEff/dh.
        // The last term is not optional now that a fade depends on h. Dropping it does not merely make
        // the reported gain inaccurate — the gain is what the detail amplitude and the rock-slope rule
        // read, so a fade steep enough to invert the surface would be reported as a gentle one.
        const dMix = mix * sFade * smoothstepSlope(band, band + step, arel) * Math.sign(rel);
        this.terraceGain = 1 + (tPrime - 1) * mixEff + (stepped - h) * dMix;
        return h + (stepped - h) * mixEff;
    }
    /**
     * Vertical wobble applied AFTER terracing so treads are not dead-flat machined shelves. Multiplicative
     * about sea level (as in the reference) rather than additive: lowlands and the coast stay calm while high
     * benches undulate, which is where the machined look would otherwise be obvious.
     */
    applyStepWobble(h, worldX, worldZ) {
        const t = this.cfg;
        const amt = t.landformStepWobble ?? 0;
        if (amt <= 0)
            return h;
        const sea = t.groundLevel;
        const rel = h - sea;
        const arel = Math.abs(rel);
        const band = this.beachBandVox();
        if (arel <= band)
            return h; // leave the shoreline alone, as terracing does
        const w = (this.wobble.GetNoise(worldX, worldZ) + 1) * 0.5; // [0,1]
        // Faded in across the band edge for the same reason terracing is: a hard cut here steps the
        // ground along the shoreline contour too. Smaller (<= 0.5 m at the defaults) and outside the
        // step-coverage gate, so it is not the reported artifact — but it is the same defect.
        return sea + rel * (1 + w * amt * smoothstep(band, band * 2, arel));
    }
    /**
     * Map the continental noise n ∈ [-1,1] to an absolute height in voxels via the configurable elevation
     * curve. The curve outputs a normalized offset relative to sea level: negative offsets scale by the
     * sea depth (ocean floor), positive by the mountain height. Finally the whole relief is scaled about
     * sea level by master×land scale, so enlarging the world stretches height and width together (slopes
     * preserved). The default curve gives a soft sea→beach ramp (no waterline cliff).
     */
    elevationCurve(n, flatness = 0.5) {
        const t = this.cfg;
        const sea = t.groundLevel;
        const u = (n + 1) * 0.5; // 0..1
        // Sea-coverage remap: stretch the input so the noise percentile (seaUThreshold) lands on the curve's
        // sea crossing (seaU0) → exactly `seaCoveragePercent` of columns below sea, curve shape preserved.
        const uT = this.seaUThreshold, u0 = this.seaU0;
        const ur = u <= uT ? (uT > 0 ? (u / uT) * u0 : u0)
            : (uT < 1 ? u0 + ((u - uT) / (1 - uT)) * (1 - u0) : u0);
        let off = this.curveFn.eval(ur); // normalized offset (~ -1 .. +1)
        // Flatness: a noise-varying EXPONENT on the normalised LAND height. Above 1 it squashes a region toward
        // lowland; below 1 it pushes it up into mountains. Because the driver is low-frequency, mountains
        // cluster into ranges with genuine flatland and shallow sea between them.
        //
        // An exponent, and not a curve reshape, for a specific reason: flattening the curve's mid-band also
        // lowers the peaks, because the FBm input rarely exceeds ~0.86 of its range so the mountain ramp falls
        // out of reach (measured: mountains 10% → 1.8% but peaks 825 m → 682 m). This leaves the peaks intact
        // and only changes how much of the world reaches them.
        //
        // LAND BRANCH ONLY, so the curve's sea crossing — and therefore computeSeaRemap's percentile and the
        // sea-coverage guarantee — is untouched.
        const flat = t.landformFlatnessAmount ?? 0;
        if (off > 0 && flat > 0) {
            // flatness=1 → exponent 0.6 (mountainous), 0 → 2.0 (plains); `flat` fades the spread toward 1 (off).
            const exponent = 1 + (LANDFORM_FLATNESS_SPREAD[0] + flatness * (LANDFORM_FLATNESS_SPREAD[1] - LANDFORM_FLATNESS_SPREAD[0]) - 1) * Math.min(1, flat);
            off = Math.pow(off, exponent);
        }
        // Ocean depth is a fraction of the mountain relief, so one master height knob scales both directions.
        const seaDepth = t.landformSeaDepthFrac * t.landformMountainHeight;
        const raw = off >= 0 ? sea + off * t.landformMountainHeight : sea + off * seaDepth;
        return sea + (raw - sea) * this.sizeScale; // vertical scale pivots on sea level
    }
    /** Landform surface slope as tan(angle): central difference of the SMOOTH macro height (so detail bumps
     *  don't register as cliffs; terracing is folded in afterwards via terraceGain). Memoized per column.
     *  `flatness` is this column's own value, threaded in so the four probes don't each re-sample it — the
     *  result stays a pure function of (worldX, worldZ) and so remains order-independent. */
    slope(worldX, worldZ, flatness = -1) {
        let inner = this.slopeCache.get(worldX);
        if (inner) {
            const c = inner.get(worldZ);
            if (c !== undefined)
                return c;
        }
        const step = 4; // meters
        // SMOOTH macro on purpose: the terrace risers are near-vertical and the treads perfectly flat, so
        // sampling the stepped field here would report either 0 or ~90° depending on where the probes landed,
        // and detail amplitude would flicker between glassy and maximally jagged across a tread boundary.
        const f = flatness >= 0 ? flatness : this.flatnessAt(worldX, worldZ);
        const hL = this.macroSmooth(worldX - step, worldZ, f);
        const hR = this.macroSmooth(worldX + step, worldZ, f);
        const hD = this.macroSmooth(worldX, worldZ - step, f);
        const hU = this.macroSmooth(worldX, worldZ + step, f);
        // heights are voxels → meters via VOXEL_SCALE; horizontal span is 2·step meters.
        const dhx = ((hR - hL) * VOXEL_SCALE) / (2 * step);
        const dhz = ((hU - hD) * VOXEL_SCALE) / (2 * step);
        const slope = Math.sqrt(dhx * dhx + dhz * dhz);
        if (this.slopeCacheEntries > 262144) {
            this.slopeCache.clear();
            this.slopeCacheEntries = 0;
            inner = undefined;
        }
        if (!inner) {
            inner = new Map();
            this.slopeCache.set(worldX, inner);
        }
        inner.set(worldZ, slope);
        this.slopeCacheEntries++;
        return slope;
    }
    /** POST-detail surface angle in degrees: central difference of the full detailed height over a short
     *  (~2 m) span, so the rubble detail registers. Drives the grass→rock material test — rock shows on
     *  the jagged detailed faces, not just the smooth macro grade. Memoized per column. */
    detailedSlopeDeg(worldX, worldZ) {
        let inner = this.detailSlopeCache.get(worldX);
        if (inner) {
            const c = inner.get(worldZ);
            if (c !== undefined)
                return c;
        }
        const step = 2; // meters — short span to capture the detail bumps, not just the macro grade
        // Probe through the memoized sampleHeight, not sampleLandformHeight directly: this is only
        // reachable with landformEnabled (all callers are gated), so the value is identical, but the
        // probes now hit heightCache instead of re-running macro+slope+detail per probe. The offsets are
        // whole voxel multiples (2 m = 8 voxels), so they land on real column keys.
        const hL = this.heightAt(worldX - step, worldZ);
        const hR = this.heightAt(worldX + step, worldZ);
        const hD = this.heightAt(worldX, worldZ - step);
        const hU = this.heightAt(worldX, worldZ + step);
        const dhx = ((hR - hL) * VOXEL_SCALE) / (2 * step);
        const dhz = ((hU - hD) * VOXEL_SCALE) / (2 * step);
        const deg = Math.atan(Math.sqrt(dhx * dhx + dhz * dhz)) * (180 / Math.PI);
        if (this.detailSlopeCacheEntries > 262144) {
            this.detailSlopeCache.clear();
            this.detailSlopeCacheEntries = 0;
            inner = undefined;
        }
        if (!inner) {
            inner = new Map();
            this.detailSlopeCache.set(worldX, inner);
        }
        inner.set(worldZ, deg);
        this.detailSlopeCacheEntries++;
        return deg;
    }
    /** Surface material for the landform layer: seabed → beach shelf → STEEP ROCK on cliffs (even up
     *  high) → snow above the snow line → ground elsewhere. Slope is the memoized macro-gradient angle
     *  so detail bumps don't flip flat ground to rock. All five come from config (`surface` in the
     *  catalog) — they were `mat()` literals, which is why the dialog could never reach them. */
    surfaceMaterial(heightVoxels, worldX, worldZ) {
        const t = this.cfg;
        const vscale = this.sizeScale; // heights are vertically scaled → scale the bands too
        const rel = heightVoxels - t.groundLevel; // voxels above sea level
        if (rel < 0)
            return t.surfaceSeabedMat; // sea floor
        if (rel <= this.beachBandVox())
            return t.surfaceBeachMat; // beach band
        // Steep rock OVERRIDES snow: any steep face is bare rock, even up high. Snow then only shows above
        // the snow line on the remaining SHALLOW slopes (caps). Uses the POST-DETAIL slope so the detailed
        // rubble faces read as rock (the detail-wavelength floor keeps this from aliasing at small scale).
        const rockDeg = t.landformRockSlopeDeg > 0 ? t.landformRockSlopeDeg : 32;
        if (this.detailedSlopeDeg(worldX, worldZ) >= rockDeg)
            return t.surfaceSteepMat;
        // Snow line is a fraction of the relief, so raising mountain height keeps the same proportion of the
        // world under snow instead of burying (or stranding) the caps.
        if (rel >= t.landformSnowLineFrac * t.landformMountainHeight * vscale)
            return t.surfaceSnowMat;
        return t.surfaceGroundMat; // plains and hillsides everywhere else
    }
    /** True where the visible landform surface is walkable ground for stamps — the ground or snow band
     *  (not sea, beach, or steep rock). Trees/rocks/buildings only scatter here. */
    isStampSurface(worldX, worldZ) {
        return this.isStampSurfaceMaterial(this.surfaceMaterial(this.heightAt(worldX, worldZ), worldX, worldZ));
    }
    /** The surface-material rule behind isStampSurface, shared with the coarse column loop (which has
     *  already computed the material and must gate the canopy shell on the SAME rule the scatter uses,
     *  or the far shell appears where no tree ever would). */
    isStampSurfaceMaterial(m) {
        // Against the CONFIGURED ids, not moss2/snow: with the skin tunable, a literal here would keep
        // testing for a material the surface no longer uses and quietly stop scattering anything.
        return m === this.cfg.surfaceGroundMat || m === this.cfg.surfaceSnowMat;
    }
}
/** One noise generator, FBm with `octaves`. The shape every landform field shares. */
function fnl(seed, octaves, type = FastNoiseLite.FractalType.FBm) {
    const n = new FastNoiseLite(seed);
    n.SetNoiseType(FastNoiseLite.NoiseType.OpenSimplex2);
    n.SetFractalType(type);
    n.SetFractalOctaves(octaves);
    return n;
}
//# sourceMappingURL=LandformField.js.map